import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { agentPhotoAPI } from '@/lib/api';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import ImageCropperSquare from '@/components/common/ImageCropperSquare';

const ACCEPTED_MIMES = 'image/jpeg,image/png,image/webp';
const MAX_SIZE = 25 * 1024 * 1024; // 25 MB

/**
 * Phase 27.30.21 — Shared avatar for agents.
 *
 * When the agent has a profile photo (`photo_url`, uploaded manually or
 * imported from Google), we fetch it through the auth-safe blob hook
 * (the streaming endpoint needs a Bearer token that a plain <img> tag
 * cannot send). Otherwise we render the classic navy-gradient initials
 * tile so the UI never shows a broken image.
 *
 * Phase 27.30.22 — Optional inline upload:
 *   Pass `editable` and the avatar becomes a click target that opens
 *   the file picker.
 *
 * Phase 27.38 — Optional inline REMOVE:
 *   When `editable` is true AND a photo is present, a small "×" button
 *   appears in the top-right corner on hover. Clicking it reverts the
 *   agent to their initials tile via `DELETE /agents/{npn}/photo`.
 *   Confirmation is intentionally lightweight (a single click) since
 *   the underlying data is trivially recoverable — the admin can just
 *   re-upload the same file.
 *
 * Props:
 *   agent      { first_name, last_name, npn, photo_url } — required
 *   size       Tailwind size class combo (default: "w-16 h-16")
 *   rounded    "lg" | "full" (default: "lg" — matches the KR card system)
 *   textSize   Tailwind text-size class for the fallback initials
 *   className  extra utility classes for the wrapper
 *   testId     data-testid override (default: "agent-avatar")
 *   editable   when true, clicking opens a file picker and a × button
 *              appears when a photo is present so admins can remove it.
 *   onChanged  fired after a successful upload OR delete with the new
 *              photo_url (null when the photo was removed).
 */
export function AgentAvatar({
  agent,
  size = 'w-16 h-16',
  rounded = 'lg',
  textSize = 'text-xl',
  className = '',
  testId = 'agent-avatar',
  editable = false,
  onChanged,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // file awaiting crop
  const photoUrl = agent?.photo_url || null;
  const blobUrl = useInternalCoverBlob(photoUrl);
  const initials = `${(agent?.first_name || '').charAt(0)}${(agent?.last_name || '').charAt(0)}`.toUpperCase() || '?';
  const roundedCls = rounded === 'full' ? 'rounded-full' : 'rounded-lg';
  const busy = uploading || removing;
  const canRemove = editable && !!blobUrl && !!agent?.npn;

  // Pick a file → open the square cropper (don't upload raw).
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !agent?.npn) return;
    if (!ACCEPTED_MIMES.split(',').includes(file.type)) {
      toast.error('Unsupported file type. Please choose a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Image is too large. Maximum size is 25 MB.');
      return;
    }
    setPendingFile(file);
  };

  const uploadCropped = async (croppedFile) => {
    if (!croppedFile || !agent?.npn) return;
    setUploading(true);
    try {
      const res = await agentPhotoAPI.adminUpload(agent.npn, croppedFile);
      toast.success('Profile photo updated');
      setPendingFile(null);
      onChanged?.(res.data?.photo_url || null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!agent?.npn || removing) return;
    setRemoving(true);
    try {
      await agentPhotoAPI.adminRemove(agent.npn);
      toast.success('Profile photo removed');
      onChanged?.(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Remove failed';
      toast.error(detail);
    } finally {
      setRemoving(false);
    }
  };

  const inner = (
    <>
      {blobUrl ? (
        <img
          src={blobUrl}
          alt={`${agent?.first_name || ''} ${agent?.last_name || ''}`.trim() || 'Agent'}
          className="w-full h-full object-cover"
          data-testid={`${testId}-image`}
        />
      ) : (
        <span className={textSize} data-testid={`${testId}-initials`}>{initials}</span>
      )}
    </>
  );

  if (editable) {
    // The avatar itself stays a clean, uncovered image. Upload / remove
    // controls live in clearly-labelled buttons BELOW the avatar so they
    // never obscure the profile photo and are obvious on touch devices.
    return (
      <div className={`flex flex-col items-center gap-2 flex-shrink-0 ${className}`} data-testid={`${testId}-editable`}>
        <div
          className={`relative ${size} ${roundedCls} overflow-hidden flex items-center justify-center font-semibold font-heading text-white`}
          style={{ background: 'linear-gradient(135deg,var(--kr-navy-700),var(--kr-navy-900))' }}
          data-testid={testId}
        >
          {inner}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => { if (!busy) inputRef.current?.click(); }}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 transition-colors disabled:opacity-50"
            data-testid={`${testId}-upload-trigger`}
          >
            {uploading
              ? <Loader2 size={12} className="animate-spin" />
              : <Camera size={12} />}
            {blobUrl ? 'Change photo' : 'Upload photo'}
          </button>
          {canRemove && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 transition-colors disabled:opacity-50"
              aria-label="Remove profile photo"
              title="Remove photo — revert to initials"
              data-testid={`${testId}-remove-btn`}
            >
              {removing
                ? <Loader2 size={12} className="animate-spin" />
                : <Trash2 size={12} />}
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIMES}
          onChange={handleFile}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        <ImageCropperSquare
          file={pendingFile}
          onCropped={uploadCropped}
          onCancel={() => setPendingFile(null)}
          title="Adjust profile photo"
        />
      </div>
    );
  }

  return (
    <div
      className={`relative ${size} ${roundedCls} overflow-hidden flex items-center justify-center font-semibold font-heading text-white flex-shrink-0 ${className}`}
      style={{ background: 'linear-gradient(135deg,var(--kr-navy-700),var(--kr-navy-900))' }}
      data-testid={testId}
    >
      {inner}
    </div>
  );
}

export default AgentAvatar;
